import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { DESK } from '../lib/layout'

// What the two side panels share, so the composer's Thoughts and the reader's
// Taken read as one system (design 4a): a 48px rail with a small tile and a
// vertical label, a panel with a title row and a close ×, Burrow's cards, and
// on phones a floating rose button (the app's capture button) that opens the
// panel as a sheet.

/** Where a side panel becomes a rail (below it: a sheet): the desk layout, lib/layout.ts. */
export const SIDE_PANEL_DESK = DESK

/** The collapsed rail's button: a small tile over a vertical label ("THOUGHTS · 1,204"). */
export function RailTab({
  label,
  icon,
  ariaLabel,
  onOpen,
  disabled = false,
  title,
}: {
  label: string
  icon: ReactNode
  ariaLabel: string
  onOpen?: () => void
  disabled?: boolean
  title?: string
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={disabled}
      title={title}
      aria-expanded={disabled ? undefined : false}
      aria-label={ariaLabel}
      className="group flex flex-col items-center gap-3.5 rounded-lg px-1.5 py-1 outline-none disabled:cursor-default"
    >
      <span
        aria-hidden
        className="flex size-[26px] items-center justify-center rounded-lg border border-border/40 bg-card transition-colors group-enabled:group-hover:border-accent/40"
      >
        {icon}
      </span>
      <span
        aria-hidden
        className="font-sans text-[11.5px] font-semibold tracking-[0.08em] text-muted-foreground [writing-mode:vertical-rl] group-enabled:group-hover:text-foreground"
      >
        {label}
      </span>
    </button>
  )
}

/** A panel's title row, with its close ×. */
export function PanelHeader({ children, closeLabel, onClose }: { children: ReactNode; closeLabel: string; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 text-[14px] font-semibold text-foreground">
      <div className="min-w-0">{children}</div>
      <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        className="-mr-1.5 flex size-7 shrink-0 items-center justify-center rounded-xl text-[18px] font-light leading-none text-muted-foreground hover:bg-muted/30 hover:text-foreground"
      >
        ×
      </button>
    </div>
  )
}

/** A card in a side panel (a saved thought, a taken line): the app's card surface: 16px radius, hairline border. */
export const panelCard = 'flex flex-col gap-2 rounded-2xl border border-card-edge bg-card px-4 pb-3.5 pt-4 transition-shadow'

export function PanelEmpty({ children }: { children: ReactNode }) {
  return <div className="py-6 text-center text-[13px] text-muted-foreground">{children}</div>
}

/**
 * Phones: the floating rose button that opens a panel's sheet. `lift` raises
 * it (px) above a bottom bar it would otherwise sit on.
 */
export function SheetFab({ children, lift = 0, className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { lift?: number }) {
  return (
    <button
      type="button"
      {...rest}
      style={lift ? { bottom: 20 + lift } : undefined}
      className={`fixed bottom-5 right-4 z-30 mb-[env(safe-area-inset-bottom)] flex items-center gap-2.5 rounded-full bg-accent py-3 pl-3.5 pr-[18px] text-[13px] font-semibold text-accent-foreground shadow-lg shadow-accent/30 ${className}`}
    >
      <span aria-hidden className="size-2 rounded-full bg-accent-foreground/70" />
      {children}
    </button>
  )
}
